import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Label, Form, Alert, Input, FormFeedback } from 'reactstrap';
import neoLogo from "../../assets/images/Neo_Logo_A.svg";
import neoLogoC from "../../assets/images/Neo_Logo_C.svg";
import bgneo from "../../assets/images/bgneo.png";
import LoadingButton from "../../components/Common/LoadingButton";
import { useAuth } from "../../hooks/useAuth";
import { auth } from "../../services/firebase";
import { formatIsoDatePtBr } from "../../utils/tenantLifecycle";
import TenantAccessBlockedModal from "./TenantAccessBlockedModal";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

const Login = () => {
  document.title = "Login | NeoApp - Sistema de Gestão";
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [errorMSG, setErrorMSG] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [inactiveTenantData, setInactiveTenantData] = useState(null);
  const [invoicesModalOpen, setInvoicesModalOpen] = useState(false);

  React.useEffect(() => {
    const blockedTenant = location.state?.blockedTenant;
    if (!blockedTenant) return;

    setInactiveTenantData(blockedTenant);
    setErrorMSG(
      blockedTenant.blockMessage
      || (
        blockedTenant.blockReason === "trial_expired" && blockedTenant.trialEndsAt
          ? `Seu periodo de teste expirou em ${formatIsoDatePtBr(blockedTenant.trialEndsAt)}.`
          : "Sua conta esta temporariamente bloqueada para acesso."
      )
    );

    setInvoicesModalOpen(true);
  }, [location.state]);



  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("E-mail inválido").required("Por favor, insira seu e-mail"),
      password: Yup.string().required("Por favor, insira sua senha"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setErrorMSG("");
      setInactiveTenantData(null);
      try {
        await login(values.email, values.password);

        // Verifica as claims do token JWT recém gerado para roteamento inteligente
        const currentUser = auth.currentUser;
        if (currentUser) {
          const tokenResult = await currentUser.getIdTokenResult(true);
          const claimFeatures = tokenResult.claims.features || [];
          const claimPermissions = tokenResult.claims.permissions || [];

          if (tokenResult.claims.role === 'super_admin') {
            navigate('/platform');
            return;
          }

          // Se a role estrita for kiosk OU se for um funcionário configurado na UI com as permissões exatas do cargo "Totem (Kiosk)"
          // A matriz padrão do "Totem (Kiosk)" salva no banco confere o acesso apenas ao 'kiosk_access'.
          const hasPerm = (perm) => Array.isArray(claimPermissions) ? claimPermissions.includes(perm) : !!claimPermissions[perm];
          const isDedicatedKiosk = (tokenResult.claims.role === 'kiosk') ||
            (tokenResult.claims.role === 'employee' && hasPerm('kiosk_access') && !hasPerm('grade_manage') && !hasPerm('members_manage'));

          if (isDedicatedKiosk && claimFeatures.includes('kiosk')) {
            navigate('/kiosk');
            return;
          }
        }

        navigate('/dashboard');
      } catch (err) {
        if (err.code === 'tenant/inactive') {
          setInactiveTenantData({
            tenantId: err.tenantId,
            tenantName: err.tenantName,
            email: values.email,
            billingStatus: err.billingStatus,
            planId: err.planId,
            blockReason: err.blockReason || "tenant_inactive",
            blockMessage: err.message,
            trialStartDate: err.trialStartDate || null,
            trialEndsAt: err.trialEndsAt || null,
            role: err.role || "employee",
            invoices: err.invoices || [],
          });
          setErrorMSG(err.message);
          setInvoicesModalOpen(true);
        } else if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
          setErrorMSG("Usuário ou senha inválidos.");
        } else if (err.message) {
          setErrorMSG(err.message);
        } else {
          setErrorMSG("Ocorreu um erro ao fazer login.");
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  return (
    <React.Fragment>
      <div className="account-pages" style={{
        backgroundImage: `url(${bgneo})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#fef5ef',
        height: '100vh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0
      }}>
        <div className="container-fluid p-0">
          <Row className="g-0 vh-100">
            {/* Left side: Transparent spacer */}
            <Col lg={8} xl={9} className="d-none d-lg-block position-relative">
              <img
                src={neoLogoC}
                alt="NeoApp Logo"
                style={{
                  position: 'absolute',
                  bottom: '48px',
                  left: '48px',
                  height: '60px',
                  filter: 'brightness(0) saturate(100%) invert(18%) sepia(51%) saturate(2338%) hue-rotate(18deg) brightness(94%) contrast(92%)',
                  opacity: 0.95
                }}
              />
            </Col>

            {/* Right side: Translucent Login Form */}
            <Col lg={4} xl={3} className="d-flex align-items-center neo-auth-col">
              <div className="p-4 p-sm-5 w-100">
                <div className="mb-5 text-center">
                  <Link to="/" className="d-block auth-logo">
                    <img src={neoLogo} alt="NeoApp" height="38" className="auth-logo-dark mx-auto" />
                  </Link>
                </div>

                <div className="mb-4">
                  <h4 className="neo-auth-title">Bem-vindo de volta!</h4>
                  <p className="neo-auth-subtitle">Acesse sua conta para continuar.</p>
                </div>

                <Form
                  className="form-horizontal"
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  {errorMSG ? (
                    <Alert className="neo-auth-alert" isOpen={!!errorMSG} fade={false}>
                      {errorMSG}
                    </Alert>
                  ) : null}

                  {location.state?.passwordReset && !errorMSG ? (
                    <Alert color="success" fade={false}>
                      <i className="mdi mdi-check-circle-outline me-2"></i>
                      Senha redefinida com sucesso! Faça login com sua nova senha.
                    </Alert>
                  ) : null}

                  <div className="mb-3">
                    <Label htmlFor="email" className="neo-auth-label">E-mail</Label>
                    <Input
                      name="email"
                      className="neo-auth-input"
                      placeholder="Digite seu e-mail"
                      type="email"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email || ""}
                      invalid={
                        validation.touched.email && validation.errors.email ? true : false
                      }
                    />
                    {validation.touched.email && validation.errors.email ? (
                      <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="userpassword" className="neo-auth-label">Senha</Label>
                    <Input
                      name="password"
                      value={validation.values.password || ""}
                      type="password"
                      className="neo-auth-input"
                      placeholder="Digite sua senha"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        validation.touched.password && validation.errors.password ? true : false
                      }
                    />
                    {validation.touched.password && validation.errors.password ? (
                      <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3 text-end">
                    <Link to="/forgot-password" className="neo-auth-link" style={{ fontSize: '0.85rem' }}>
                      Esqueci minha senha
                    </Link>
                  </div>

                  <div className="mt-4 d-grid">
                    <LoadingButton
                      className="neo-auth-btn-primary"
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      Entrar
                    </LoadingButton>
                  </div>
                </Form>

                <div className="mt-5 text-center text-muted">
                  <p className="mb-0">© {new Date().getFullYear()} NeoApp</p>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <TenantAccessBlockedModal
        isOpen={invoicesModalOpen}
        toggle={() => setInvoicesModalOpen(!invoicesModalOpen)}
        tenantData={inactiveTenantData}
      />
    </React.Fragment>
  )
}

export default Login;
